import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import NotificationBell from '../feed/components/NotificationBell';
import useAuthStore, { isTokenExpired } from '../stores/authStore';
import './Header.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const SERVER_ORIGIN = API_BASE.replace(/\/api$/, '');

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('/img/')) {
    return url;
  }
  return `${SERVER_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const Header = ({ darkMode, setDarkMode }) => {
  const navigate = useNavigate();
  const { user, token, signout } = useAuthStore();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userMenuRef = useRef(null);

  const isAuthenticated = !!token && !isTokenExpired(token);
  const isEmployer = isAuthenticated && user?.accountType === 'employer';

  // Header is rendered on every page — check token expiry here.
  // If expired, sign out silently so the nav switches to guest state.
  useEffect(() => {
    if (token && isTokenExpired(token)) {
      signout();
    }
  }, [token, signout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    setShowUserDropdown(false);
    signout();
    navigate('/signin');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setShowMobileMenu(false);
    setShowUserDropdown(false);
  };

  const toggleMoreDropdown = () => {
    setShowMoreDropdown(!showMoreDropdown);
  };

  const closeMoreDropdown = () => {
    setShowMoreDropdown(false);
  };

  const services = [
    { label: 'Find Co-founder', path: '/search' },
    { label: 'Community Feed', path: '/feed' },
    { label: 'MVP Development', path: '/mvp-development' },
    { label: 'Startup Jobs', path: '/startup-jobs' }
  ];

  const company = [
    { label: 'About Us', path: '/about' },
    { label: 'Vision', path: '/vision' },
    { label: 'Careers', path: '/careers' }
  ];

  const resources = [
    { label: 'Pricing', path: '/pricing' },
    { label: 'Help Center', path: '/help' },
    { label: 'Contact Us', path: '/contact' },
    { label: 'Other', path: '/other' }
  ];

  return (
    <header className="header">
      <div className="header-content">
        <Link to="/" className="header-title-link">
          <img className="header-logo" src="/co-founder.png" alt="CofounderHubs logo" />
          <h1 className="header-title">CofounderHubs</h1>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          <NavLink 
            to="/" 
            end
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            Home
          </NavLink>
          
          <NavLink 
            to="/search"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            Find
          </NavLink>

          <NavLink 
            to="/feed"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            Feed
          </NavLink>

          <NavLink 
            to="/startup-jobs"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            Jobs
          </NavLink>

          <NavLink 
            to="/about"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            About Us
          </NavLink>

          <NavLink 
            to="/contact"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            Contact Us
          </NavLink>

          {/* More Dropdown - Multi-column menu */}
          <div className="nav-dropdown more-dropdown">
            <button 
              className="nav-link dropdown-toggle"
              onClick={toggleMoreDropdown}
            >
              More
              <svg className={`dropdown-chevron ${showMoreDropdown ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {showMoreDropdown && (
              <div className="dropdown-menu dropdown-menu-columns">
                {/* Services Column */}
                <div className="dropdown-column">
                  <div className="dropdown-column-title">Services</div>
                  {services.map((service) => (
                    <NavLink
                      key={service.path}
                      to={service.path}
                      className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                      onClick={closeMoreDropdown}
                    >
                      {service.label}
                    </NavLink>
                  ))}
                </div>

                {/* Company Column */}
                <div className="dropdown-column">
                  <div className="dropdown-column-title">Company</div>
                  {company.map((item) => (
                    item.path.startsWith('http') ? (
                      <a
                        key={item.path}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dropdown-item"
                        onClick={closeMoreDropdown}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={closeMoreDropdown}
                      >
                        {item.label}
                      </NavLink>
                    )
                  ))}
                </div>

                {/* Resources Column */}
                <div className="dropdown-column">
                  <div className="dropdown-column-title">Resources</div>
                  {resources.map((resource) => (
                    resource.path.startsWith('http') ? (
                      <a
                        key={resource.path}
                        href={resource.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dropdown-item"
                        onClick={closeMoreDropdown}
                      >
                        {resource.label}
                      </a>
                    ) : (
                      <NavLink
                        key={resource.path}
                        to={resource.path}
                        className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={closeMoreDropdown}
                      >
                        {resource.label}
                      </NavLink>
                    )
                  ))}
                </div>

                {/* Hero Image */}
                <div className="dropdown-hero-image">
                  <img src="/img/startup-pen.jpg" alt="CofounderHubs" />
                </div>
              </div>
            )}
          </div>
        </nav>
        
        {/* Mobile Navigation Dropdown */}
        {showMobileMenu && (
          <nav className="header-nav mobile-nav">
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/')}
            >
              Home
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/search')}
            >
              Find
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/feed')}
            >
              Feed
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/startup-jobs')}
            >
              Jobs
            </button>
            {isEmployer ? (
              <>
                <button
                  className="mobile-nav-item"
                  onClick={() => handleNavClick('/post-job')}
                >
                  Post a Job
                </button>
                <button
                  className="mobile-nav-item"
                  onClick={() => handleNavClick('/post-job/status')}
                >
                  My Jobs
                </button>
              </>
            ) : null}
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/about')}
            >
              About Us
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/contact')}
            >
              Contact Us
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/pricing')}
            >
              Pricing
            </button>
            <button 
              className="mobile-nav-item"
              onClick={() => handleNavClick('/help')}
            >
              Help
            </button>
            <div className="mobile-nav-section">
              <div className="mobile-nav-label">Services</div>
              {services.map((service) => (
                <button
                  key={service.path}
                  className="mobile-nav-item mobile-nav-subitem"
                  onClick={() => handleNavClick(service.path)}
                >
                  {service.label}
                </button>
              ))}
            </div>
            <div className="mobile-nav-section">
              <div className="mobile-nav-label">Company</div>
              {company.map((item) => (
                item.path.startsWith('http') ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobile-nav-item mobile-nav-subitem"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.path}
                    className="mobile-nav-item mobile-nav-subitem"
                    onClick={() => handleNavClick(item.path)}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>
            <div className="mobile-nav-section">
              <div className="mobile-nav-label">Resources</div>
              {resources.map((resource) => (
                resource.path.startsWith('http') ? (
                  <a
                    key={resource.path}
                    href={resource.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobile-nav-item mobile-nav-subitem"
                  >
                    {resource.label}
                  </a>
                ) : (
                  <button
                    key={resource.path}
                    className="mobile-nav-item mobile-nav-subitem"
                    onClick={() => handleNavClick(resource.path)}
                  >
                    {resource.label}
                  </button>
                )
              ))}
            </div>
          </nav>
        )}
        

        <div className="header-controls">
          {isAuthenticated ? (
            <>
              {isEmployer ? (
                <button
                  type="button"
                  className="header-post-job-btn"
                  onClick={() => navigate('/post-job')}
                >
                  Post a Job
                </button>
              ) : null}
              <div
                className={`user-menu${showUserDropdown ? ' user-menu-open' : ''}`}
                ref={userMenuRef}
              >
                <button
                  type="button"
                  className="user-avatar-btn"
                  aria-label="Open account menu"
                  aria-expanded={showUserDropdown}
                  onClick={() => setShowUserDropdown((open) => !open)}
                >
                  <img
                    src={user?.avatarUrl && user.avatarUrl.trim() ? resolveMediaUrl(user.avatarUrl) : '/img/user-man.png'}
                    alt={user?.username || 'User'}
                    className="user-avatar"
                    onError={(e) => {
                      e.target.src = '/img/user-man.png';
                    }}
                  />
                </button>
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-username">{user?.username}</div>
                    <div className="dropdown-level">
                      {isEmployer ? 'Company account' : `Level ${user?.level || 0}`}
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  {isEmployer ? (
                    <>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/post-job/status')}
                      >
                        My Jobs
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/post-job')}
                      >
                        Post a Job
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/profile')}
                      >
                        Profile Settings
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/profile')}
                      >
                        Profile Settings
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/connections')}
                      >
                        My Network
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => handleNavClick('/profile#upgrade-level')}
                      >
                        Upgrade Level
                      </button>
                    </>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item" onClick={handleSignOut}>
                    Sign Out
                  </button>
                </div>
              </div>
              <NotificationBell />
            </>
          ) : (
            <>
              <NavLink 
                to="/signin"
                className={({ isActive }) => `nav-link auth ${isActive ? 'active' : ''}`}
              >
                Sign In
              </NavLink>
              <NavLink 
                to="/signup"
                className={({ isActive }) => `nav-link auth ${isActive ? 'active' : ''}`}
              >
                Sign Up
              </NavLink>
            </>
          )}

          <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />
          
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;